import React, { useEffect } from 'react';
import { useIsMobile } from '@guildeducationinc/catalog-tools';
import { Typography, ShowMore } from '@guildeducationinc/recess';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import {
	GetRolesForProgramsQuery,
	GetProgramOutQuery,
} from 'src/graphqlTypes/roleProgramGraphServiceTypes';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import { useDecision } from '@optimizely/react-sdk';
import { ROLE_MAPPING, getRolesToDisplay, RoleVariaions } from '@src/constants/featureFlags';
import { addDataDogUserAttribute } from '@src/utilities/datadog';
import { Pathway } from './Pathway';
import styles from './CareerPathways.module.scss';

interface Props {
	loading: boolean;
	program: GetMemberProgramQuery['memberProgram'];
	rolesForPrograms: GetRolesForProgramsQuery['rolesForPrograms'];
	destinationRoles?: GetProgramOutQuery['getProgramOut'];
}

export interface PathwayWithRoles {
	pathwayId: string;
	pathwayName: string;
	roles: {
		id: string;
		name: string;
		genericName?: string;
		title?: string;
	}[];
}

export const ROLES_DISCLAIMER =
	"We can't promise that you'll earn any specific role in these or other fields upon completing this program. But our programs do offer high-quality training in skills related to these fields. Connect with your employer about how this program might enhance your job or career.";

export const CareerPathways: React.FC<Props> = ({
	loading,
	program,
	rolesForPrograms,
	destinationRoles,
}) => {
	const isMobile = useIsMobile();
	const [rolesDecision] = useDecision(ROLE_MAPPING);
	const roleVariation = getRolesToDisplay(rolesDecision);

	useEffect(() => {
		if (
			roleVariation === RoleVariaions.DESTINATION &&
			destinationRoles &&
			destinationRoles.length > 0
		) {
			addDataDogUserAttribute('destination_roles_variation_viewed', true);
		}
	}, [roleVariation, destinationRoles]);

	const pathwaysMap: { [key: string]: PathwayWithRoles } = {};

	if (roleVariation === RoleVariaions.DESTINATION && destinationRoles) {
		destinationRoles.forEach(role => {
			const { pathwayId, pathwayName } = role;
			if (!pathwaysMap[pathwayId]) {
				pathwaysMap[pathwayId] = {
					pathwayId,
					pathwayName,
					roles: [],
				};
			}
			pathwaysMap[pathwayId].roles.push({
				id: role.id,
				name: role.title,
				title: role.title,
			});
		});
	} else {
		rolesForPrograms[0]?.roles.forEach(role => {
			const { pathwayId, pathwayName } = role;
			if (!pathwaysMap[pathwayId]) {
				pathwaysMap[pathwayId] = {
					pathwayId,
					pathwayName,
					roles: [],
				};
			}
			pathwaysMap[pathwayId].roles.push({
				id: role.id,
				name: role.name,
				genericName: role.genericName,
			});
		});
	}

	const pathways = Object.values(pathwaysMap);
	const hasFewerThanThreePathways = pathways.length <= 2;

	const pathwaysList = pathways.map(pathway => (
		<Pathway key={pathway.pathwayId} pathway={pathway} />
	));

	return (
		<section
			aria-label='career pathways'
			className={styles.sectionContainer}
			data-testid='career-pathways'
			data-title='Career Pathways'
			id='detail-career-pathways'
			tabIndex={-1}
		>
			<Skeleton count={3} dataLoading={loading} height={64}>
				<Typography className={styles.header} variant='h2'>
					Career Pathways
				</Typography>
				<Typography
					className={styles.subhead}
					testid='concentrations-subhead'
					variant={isMobile ? 'body1' : 'body2'}
				>
					Apply the skills you learn in {program?.studentFacingAcademicPartner?.name}&apos;s{' '}
					{program?.name} to roles in these career pathways.
				</Typography>
				{pathways.length <= 2 ? (
					<>
						{pathwaysList}
						<Typography className={styles.disclaimer} variant='body1'>
							{ROLES_DISCLAIMER}
						</Typography>
					</>
				) : (
					<ShowMore panelId='career-pathways'>
						{pathwaysList}
						<Typography className={styles.disclaimer} variant='body1'>
							{ROLES_DISCLAIMER}
						</Typography>
					</ShowMore>
				)}
			</Skeleton>
		</section>
	);
};
